// @catalog F.006
// Layout shell — rediseño ejecutivo plan dashboard_executive_redesign 2026-05-21.
// Sidebar permanente en desktop (≥1100px) + drawer temporal en mobile.
// Topbar light con crumbs + SyncStatusBadge + bell + user.
import React, { ReactNode, useState } from "react";
import {
  Box,
  IconButton,
  Stack,
  Typography,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  AccountCircle,
  ExitToApp,
  Menu as MenuIcon,
  NavigateNext as NavigateNextIcon,
  Notifications as NotificationsIcon,
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { MenuDrawer, SIDEBAR_WIDTH } from "./MenuDrawer";
import { usePageTitle } from "../context/PageTitleContext";
import { useUser } from "../context/UserContext";
import { SyncStatusBadge } from "./analytics/SyncStatusBadge";

interface LayoutProps {
  children: ReactNode;
  signOut?: (() => void) | undefined;
}

const TOPBAR_H = 56;

// Determina sección + página actuales para crumbs.
function deriveCrumbs(pathname: string): { section: string; page: string | null } {
  if (pathname === "/" || pathname === "") return { section: "Inicio", page: null };
  if (pathname.startsWith("/ventas")) return { section: "Analytics", page: "Ventas" };
  if (pathname.startsWith("/margenes")) return { section: "Analytics", page: "Márgenes" };
  if (pathname.startsWith("/compras/forecast")) return { section: "Compras", page: "Forecast" };
  if (pathname.startsWith("/compras/marcas")) return { section: "Compras", page: "Marcas" };
  if (pathname.startsWith("/compras/categorias")) return { section: "Compras", page: "Categorías" };
  if (pathname.startsWith("/distribucion")) return { section: "Distribución", page: null };
  if (pathname.startsWith("/dist/")) return { section: "Distribución", page: null };
  if (pathname.startsWith("/odoo")) return { section: "Sistema", page: "Odoo ERP" };
  if (pathname.startsWith("/usuarios")) return { section: "Sistema", page: "Usuarios" };
  if (pathname.startsWith("/settings")) return { section: "Sistema", page: "Configuración" };
  return { section: "VillApps", page: null };
}

export function Layout({ children, signOut }: LayoutProps): React.ReactElement {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  // Sidebar permanente en desktop ≥1100px; drawer temporal en breakpoints menores.
  const isDesktop = useMediaQuery("(min-width:1100px)");
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);
  const { title } = usePageTitle();
  const { user } = useUser();

  const userEmail = user?.email || "";
  const { section, page } = deriveCrumbs(location.pathname);
  const finalPage = page || title || null;

  const handleMenuClick = (): void => {
    if (isDesktop) return;
    setIsMobileDrawerOpen(true);
  };

  return (
    <Box sx={{ minHeight: "100vh", width: "100%", backgroundColor: "var(--bg)" }}>
      {/* Sidebar: permanente en desktop, temporal en mobile */}
      <MenuDrawer
        isOpen={isDesktop ? true : isMobileDrawerOpen}
        onToggle={(open) => setIsMobileDrawerOpen(open)}
        variant={isDesktop ? "permanent" : "temporary"}
      />

      {/* Contenedor principal con offset para sidebar permanente */}
      <Box
        sx={{
          marginLeft: isDesktop ? `${SIDEBAR_WIDTH}px` : 0,
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          minWidth: 0,
        }}
      >
        {/* Topbar fijo */}
        <Box
          component="header"
          sx={{
            position: "sticky",
            top: 0,
            zIndex: theme.zIndex.appBar,
            height: `${TOPBAR_H}px`,
            backgroundColor: "var(--surface)",
            borderBottom: "1px solid var(--border)",
            display: "flex",
            alignItems: "center",
            padding: "0 24px",
            gap: "16px",
          }}
        >
          {/* Botón menú mobile */}
          {!isDesktop && (
            <IconButton
              edge="start"
              aria-label="abrir menú"
              onClick={handleMenuClick}
              size="small"
              sx={{ color: "var(--ink-2)" }}
            >
              <MenuIcon />
            </IconButton>
          )}

          {/* Crumbs */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.75}
            sx={{ color: "var(--ink-3)", fontSize: "13px", minWidth: 0 }}
          >
            {section && (
              <Box component="span" sx={{ color: "var(--ink-3)" }}>
                {section}
              </Box>
            )}
            {finalPage && (
              <>
                <NavigateNextIcon sx={{ fontSize: 14, color: "var(--ink-4)" }} />
                <Box
                  component="strong"
                  sx={{ color: "var(--ink-1)", fontWeight: 600 }}
                  title={typeof finalPage === "string" ? finalPage : undefined}
                >
                  {finalPage}
                </Box>
              </>
            )}
          </Stack>

          {/* Spacer */}
          <Box sx={{ flex: 1 }} />

          {/* Status + acciones */}
          <Stack direction="row" alignItems="center" spacing={2}>
            <SyncStatusBadge label="Sistema OK" />
            <Tooltip title="Notificaciones" arrow>
              <IconButton
                aria-label="notificaciones"
                size="small"
                sx={{
                  color: "var(--ink-2)",
                  border: "1px solid var(--border)",
                  borderRadius: "6px",
                  width: 32,
                  height: 32,
                  "&:hover": { backgroundColor: "var(--bg-2)" },
                }}
              >
                <NotificationsIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
            {userEmail && (
              <Typography
                variant="body2"
                sx={{
                  color: "var(--ink-2)",
                  fontSize: "13px",
                  fontWeight: 500,
                  display: { xs: "none", md: "inline" },
                }}
              >
                {userEmail}
              </Typography>
            )}
            <Tooltip title="Cuenta de usuario" arrow>
              <IconButton
                aria-label="cuenta de usuario"
                size="small"
                onClick={() => navigate("/")}
                sx={{ color: "var(--ink-2)" }}
              >
                <AccountCircle sx={{ fontSize: 22 }} />
              </IconButton>
            </Tooltip>
            {signOut && (
              <Tooltip title="Cerrar sesión" arrow>
                <IconButton
                  onClick={signOut}
                  aria-label="cerrar sesión"
                  size="small"
                  sx={{ color: "var(--ink-2)" }}
                >
                  <ExitToApp sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Box>

        {/* Contenido principal */}
        <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
      </Box>
    </Box>
  );
}
